import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  FolderIcon,
  FolderOpenIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { kbApi, KBArticleListItem } from '../services/kbApi';

/**
 * Yandex-Wiki-style left tree for the Knowledge Base.
 *
 * One level deep for now: top-level nodes are categories (or "Без категории"
 * for uncategorized articles), leaves are articles. Click a leaf to open.
 *
 * Persists which folders are expanded in localStorage so navigating between
 * articles doesn't collapse the user's working set.
 *
 * The tree fetches its own data (200-article window, which is plenty for an
 * internal CRM KB). If true hierarchy is later needed we can swap categories
 * for a parent_id FK without rewriting the consumers.
 */

const LS_KEY = 'kb.tree.openCategories';
const UNCAT = '__uncat__';
const UNCAT_LABEL = 'Без категории';

interface Props {
  /** Re-fetch trigger — bump when articles change so the tree refreshes. */
  refreshKey?: number;
}

const KBTreeSidebar: React.FC<Props> = ({ refreshKey = 0 }) => {
  const [articles, setArticles] = useState<KBArticleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [filter, setFilter] = useState('');
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    kbApi
      .list({ limit: 200 })
      .then(res => { if (alive) setArticles(res.articles); })
      .catch(() => { /* tree silently empty on failure */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);

  // Two-level grouping: category → subcategory → articles. Articles with no
  // subcategory live in a synthetic "" bucket rendered directly under the
  // category (no extra folder). Categories sort alphabetically, "Без
  // категории" last.
  const tree = useMemo(() => {
    const lower = filter.trim().toLowerCase();
    const matches = (a: KBArticleListItem) =>
      !lower
      || a.title.toLowerCase().includes(lower)
      || a.tags.some(t => t.toLowerCase().includes(lower));

    // catKey -> subKey -> articles[]
    const cats: Record<string, Record<string, KBArticleListItem[]>> = {};
    for (const a of articles) {
      if (!matches(a)) continue;
      const cat = (a.category || '').trim() || UNCAT;
      const sub = (a.subcategory || '').trim();
      ((cats[cat] ||= {})[sub] ||= []).push(a);
    }

    const byTitle = (a: KBArticleListItem, b: KBArticleListItem) =>
      a.title.localeCompare(b.title, 'ru');

    return Object.entries(cats)
      .map(([cat, subs]) => {
        // Direct articles (no subcategory) + named subcategory folders.
        const direct = (subs[''] || []).slice().sort(byTitle);
        const subFolders = Object.entries(subs)
          .filter(([sub]) => sub !== '')
          .map(([sub, items]) => ({
            key: `${cat}//${sub}`,
            label: sub,
            items: items.slice().sort(byTitle),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        const count = direct.length + subFolders.reduce((n, s) => n + s.items.length, 0);
        return { key: cat, label: cat === UNCAT ? UNCAT_LABEL : cat, direct, subFolders, count };
      })
      .filter(g => g.count > 0)
      .sort((a, b) => {
        if (a.key === UNCAT) return 1;
        if (b.key === UNCAT) return -1;
        return a.label.localeCompare(b.label, 'ru');
      });
  }, [articles, filter]);

  // Auto-expand everything when filtering so matches are visible.
  const effectiveOpen = useMemo(() => {
    if (!filter.trim()) return open;
    const all: Record<string, boolean> = {};
    tree.forEach(g => {
      all[g.key] = true;
      g.subFolders.forEach(s => { all[s.key] = true; });
    });
    return all;
  }, [open, filter, tree]);

  const toggle = (key: string) => {
    setOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Highlight the article currently open in the right pane.
  const currentId = useMemo(() => {
    const m = location.pathname.match(/^\/kb\/([^/]+)$/);
    return m && m[1] !== 'new' ? m[1] : null;
  }, [location.pathname]);

  return (
    <aside className="w-full lg:w-64 xl:w-72 lg:shrink-0 lg:border-r lg:border-border lg:pr-3">
      <div className="lg:sticky lg:top-2 space-y-2">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Найти в дереве…"
          className="w-full h-9 px-3 rounded-md border border-border bg-card text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />

        <nav className="text-sm max-h-[calc(100vh-12rem)] lg:overflow-y-auto pr-1">
          {loading ? (
            <div className="text-xs text-muted-foreground p-2">Загрузка…</div>
          ) : tree.length === 0 ? (
            <div className="text-xs text-muted-foreground p-2">
              {filter ? 'По фильтру ничего нет' : 'Нет статей'}
            </div>
          ) : (
            <ul className="space-y-0.5">
              {tree.map(group => {
                const isOpen = !!effectiveOpen[group.key];
                return (
                  <li key={group.key}>
                    <button
                      type="button"
                      onClick={() => toggle(group.key)}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-foreground/90 hover:bg-accent transition-colors"
                    >
                      {isOpen
                        ? <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        : <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      {isOpen
                        ? <FolderOpenIcon className="h-4 w-4 shrink-0 text-amber-500" />
                        : <FolderIcon className="h-4 w-4 shrink-0 text-amber-500/80" />}
                      <span className="truncate font-medium text-xs uppercase tracking-wide">
                        {group.label}
                      </span>
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        {group.count}
                      </span>
                    </button>
                    {isOpen && (
                      <ul className="ml-3 border-l border-border/60 pl-1 mt-0.5 space-y-0.5">
                        {/* Articles directly under the category (no subcat) */}
                        {group.direct.map(a => (
                          <ArticleLink key={a.id} a={a} currentId={currentId} />
                        ))}
                        {/* Subcategory folders */}
                        {group.subFolders.map(sub => {
                          const subOpen = !!effectiveOpen[sub.key];
                          return (
                            <li key={sub.key}>
                              <button
                                type="button"
                                onClick={() => toggle(sub.key)}
                                className="w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-left text-foreground/80 hover:bg-accent transition-colors"
                              >
                                {subOpen
                                  ? <ChevronDownIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
                                  : <ChevronRightIcon className="h-3 w-3 shrink-0 text-muted-foreground" />}
                                {subOpen
                                  ? <FolderOpenIcon className="h-3.5 w-3.5 shrink-0 text-sky-500" />
                                  : <FolderIcon className="h-3.5 w-3.5 shrink-0 text-sky-500/80" />}
                                <span className="truncate text-xs">{sub.label}</span>
                                <span className="ml-auto text-[10px] text-muted-foreground">
                                  {sub.items.length}
                                </span>
                              </button>
                              {subOpen && (
                                <ul className="ml-3 border-l border-border/60 pl-1 mt-0.5 space-y-0.5">
                                  {sub.items.map(a => (
                                    <ArticleLink key={a.id} a={a} currentId={currentId} />
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      </div>
    </aside>
  );
};

/** Single article leaf in the tree. Shared by category-direct lists and
 *  subcategory folders. */
const ArticleLink: React.FC<{ a: KBArticleListItem; currentId: string | null }>
  = ({ a, currentId }) => {
  const active = a.id === currentId || a.slug === currentId;
  return (
    <li>
      <Link
        to={`/kb/${a.id}`}
        className={`flex items-start gap-1.5 px-2 py-1 rounded-md text-xs leading-snug transition-colors
          ${active
            ? 'bg-primary/15 text-primary font-medium'
            : 'text-foreground/80 hover:bg-accent hover:text-foreground'}`}
      >
        <DocumentTextIcon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
        <span className="truncate flex-1">{a.title}</span>
        {a.status !== 'published' && (
          <span
            className={`text-[9px] px-1 py-0.5 rounded uppercase tracking-wide shrink-0 ${
              a.status === 'draft'
                ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                : 'bg-muted text-muted-foreground border border-border'
            }`}
            title={a.status === 'draft' ? 'Черновик' : 'Архив'}
          >
            {a.status === 'draft' ? 'Ч' : 'А'}
          </span>
        )}
      </Link>
    </li>
  );
};

export default KBTreeSidebar;
